import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { usePatients, useSearchPatients } from '../../hooks/usePatients';
import { PatientListDto } from '../../types/patient.types';
import { LoadingSpinner } from '../common/LoadingSpinner';
import { ErrorMessage } from '../common/ErrorMessage';
import { User, Search, Users, FileText } from 'lucide-react';
import { Input } from '../common/Input';
import { Button } from '../common/Button';
import { debounce } from '../../utils/helpers';

interface PatientSelectorProps {
  onSelect: (patient: PatientListDto) => void;
  onCancel?: () => void;
}

export const PatientSelector: React.FC<PatientSelectorProps> = ({
  onSelect,
}) => {
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();
  const [filterQuery, setFilterQuery] = useState('');

  const debouncedSetFilter = useMemo(
    () => debounce<[string]>((q) => setFilterQuery(q), 300),
    []
  );

  const handleFilterChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetFilter(e.target.value);
  };

  const { data: allPatients, isLoading: isLoadingAll, error: allError } = usePatients();
  const { data: searchResults, isLoading: isLoadingSearch } = useSearchPatients(filterQuery);

  const patients = filterQuery.trim() ? searchResults : allPatients;
  const isLoading = filterQuery.trim() ? isLoadingSearch : isLoadingAll;
  const error = filterQuery.trim() ? null : allError;

  const displayPatients = patients ?? [];

  if (error) {
    return (
      <div className="p-4" dir={isRTL ? 'rtl' : 'ltr'}>
        <ErrorMessage message={error.message || t('errors.required')} />
      </div>
    );
  }

  return (
    <div dir={isRTL ? 'rtl' : 'ltr'}>
      {/* Filter */}
      <div className="mb-4">
        <div className="position-relative">
          <Search
            className="position-absolute top-50 translate-middle-y"
            size={20}
            style={{
              [isRTL ? 'right' : 'left']: '0.75rem',
              color: '#64748b',
              pointerEvents: 'none',
            }}
          />
          <Input
            type="text"
            placeholder={t('prescriptions.searchPatient', 'ابحث بالاسم أو الهاتف أو الرقم...')}
            onChange={handleFilterChange}
            style={{
              paddingLeft: isRTL ? '0.75rem' : '2.75rem',
              paddingRight: isRTL ? '2.75rem' : '0.75rem',
            }}
          />
        </div>
      </div>

      {/* Table */}
      <div
        className="rounded-3 overflow-hidden border"
        style={{
          backgroundColor: '#fff',
          borderColor: '#e2e8f0',
          boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
        }}
      >
        {isLoading ? (
          <div className="d-flex justify-content-center align-items-center py-5">
            <LoadingSpinner />
          </div>
        ) : displayPatients.length === 0 ? (
          <div className="text-center py-5 px-3">
            <User className="text-muted mb-2" size={40} />
            <p className="text-muted mb-0">
              {filterQuery.trim()
                ? t('patients.noPatientsMatch', 'لا توجد نتائج تطابق البحث')
                : t('patients.noPatients', 'لا يوجد مرضى')}
            </p>
          </div>
        ) : (
          <div className="table-responsive">
            <table
              className="table table-hover align-middle mb-0"
              dir={isRTL ? 'rtl' : 'ltr'}
              style={{ fontSize: '0.9375rem' }}
            >
              <thead>
                <tr style={{ backgroundColor: '#f1f5f9', borderBottom: '2px solid #e2e8f0' }}>
                  <th
                    className="fw-semibold text-secondary"
                    style={{
                      width: '70px',
                      padding: '0.75rem 1rem',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {t('common.id', 'الرقم')}
                  </th>
                  <th
                    className="fw-semibold text-secondary"
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.8125rem',
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    {t('patients.name', 'الاسم')}
                  </th>
                  <th
                    className="fw-semibold text-secondary"
                    style={{
                      width: '90px',
                      padding: '0.75rem 1rem',
                      fontSize: '0.8125rem',
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    {t('patients.age', 'العمر')}
                  </th>
                  <th
                    className="fw-semibold text-secondary"
                    style={{
                      padding: '0.75rem 1rem',
                      fontSize: '0.8125rem',
                      textAlign: isRTL ? 'right' : 'left',
                    }}
                  >
                    {t('patients.phone', 'الهاتف')}
                  </th>
                  <th
                    className="fw-semibold text-secondary"
                    style={{
                      width: '100px',
                      padding: '0.75rem 1rem',
                      fontSize: '0.8125rem',
                      textAlign: 'center',
                    }}
                  >
                    {t('patients.gender', 'الجنس')}
                  </th>
                  <th
                    className="fw-semibold text-secondary text-center"
                    style={{
                      width: '140px',
                      padding: '0.75rem 1rem',
                      fontSize: '0.8125rem',
                    }}
                  >
                    {t('common.action', 'إجراء')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {displayPatients.map((patient, index) => (
                  <tr
                    key={patient.id}
                    style={{
                      backgroundColor: index % 2 === 0 ? '#fff' : '#f8fafc',
                      borderBottom: '1px solid #f1f5f9',
                    }}
                  >
                    <td
                      className="text-secondary"
                      style={{ padding: '0.75rem 1rem', fontWeight: '600' }}
                    >
                      {patient.id}
                    </td>
                    <td style={{ padding: '0.75rem 1rem' }}>
                      <div className="d-flex align-items-center gap-2">
                        <div
                          className="rounded-circle d-flex align-items-center justify-content-center text-white flex-shrink-0"
                          style={{
                            width: '36px',
                            height: '36px',
                            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                          }}
                        >
                          <Users size={18} />
                        </div>
                        <span className="fw-semibold text-dark">{patient.name}</span>
                      </div>
                    </td>
                    <td className="text-secondary" style={{ padding: '0.75rem 1rem' }}>
                      {patient.age} {t('patients.years', 'سنة')}
                    </td>
                    <td className="text-secondary" style={{ padding: '0.75rem 1rem' }}>
                      {patient.phone ?? '—'}
                    </td>
                    <td className="text-center" style={{ padding: '0.75rem 1rem' }}>
                      {patient.gender ? (
                        <span
                          className="badge"
                          style={{
                            backgroundColor:
                              patient.gender === 'Male' ? 'rgba(59, 130, 246, 0.15)' : 'rgba(236, 72, 153, 0.15)',
                            color: patient.gender === 'Male' ? '#1e40af' : '#9f1239',
                            fontSize: '0.75rem',
                          }}
                        >
                          {patient.gender === 'Male'
                            ? t('patients.male', 'ذكر')
                            : t('patients.female', 'أنثى')}
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="text-center" style={{ padding: '0.75rem 1rem' }}>
                      <Button
                        type="button"
                        variant="primary"
                        size="sm"
                        onClick={() => onSelect(patient)}
                        className="d-inline-flex align-items-center gap-1"
                      >
                        <FileText size={16} />
                        {t('prescriptions.createPrescriptionFor', 'إنشاء وصفة')}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};
